import { Auth0ContextInterface } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef } from "react";
import { parseIdToken, removeIdToken } from "../http/id-token";
import RemergeApi from "../http/RemergeApi";
import { resolveAuthMethod, ResolvedAuthMethod } from "./lifecycle";
import { buildAuth0AppState, redirectToLogin } from "./redirects";
import { getSsoAuthType, setSsoAuthType, shouldAttemptLocalAuthBootstrap } from "./sso-state";
import { AppUser } from "./types";
import { useAuth0Provision } from "./useAuth0Provision";
import { performSharedLogout } from "./useSharedLogout";

export type SharedAuthMethod = ResolvedAuthMethod;

export type UseSharedAuthLifecycleParams = {
  remergeApi: RemergeApi;
  loginBaseUrl: string;
  auth0: Pick<
    Auth0ContextInterface,
    "isLoading" | "isAuthenticated" | "loginWithRedirect" | "logout" | "user" | "error"
  >;
  onAuthenticated?: (user: AppUser, method: Exclude<SharedAuthMethod, "none">) => void;
  onUnauthenticated?: () => void;
  onProvisionError?: () => void;
};

export type UseSharedAuthLifecycleResult = {
  user: AppUser | undefined;
  isResolvingAuth: boolean;
  isAuthenticated: boolean;
  authMethod: SharedAuthMethod;
  logout: () => Promise<void>;
};

/**
 * Shared hybrid auth lifecycle orchestration for non-admin apps.
 *
 * Security invariant: unauthenticated fallback happens only after bounded probe
 * paths are exhausted and stale local auth artifacts are cleared.
 */
export function useSharedAuthLifecycle({
  remergeApi,
  loginBaseUrl,
  auth0,
  onAuthenticated,
  onUnauthenticated,
  onProvisionError,
}: UseSharedAuthLifecycleParams): UseSharedAuthLifecycleResult {
  const idToken = parseIdToken();
  const hasAttemptedAuth0Probe = useRef(false);

  const handleProvisionError = useCallback(() => {
    removeIdToken();
    onProvisionError?.();
    onUnauthenticated?.();
    redirectToLogin({ loginBaseUrl });
  }, [loginBaseUrl, onProvisionError, onUnauthenticated]);

  const { isProvisioning, provisionedUser, clearProvisionedUser, resetProvisionAttempt } = useAuth0Provision({
    isLoading: auth0.isLoading,
    isAuth0Authenticated: auth0.isAuthenticated,
    hasUser: Boolean(idToken?.user.id),
    remergeApi,
    auth0User: auth0.user,
    onProvisionError: handleProvisionError,
  });

  const user: AppUser | undefined = idToken?.user ?? provisionedUser;
  const isResolvingAuth = (auth0.isLoading && !auth0.error) || isProvisioning;
  const isAuthenticated = Boolean(user?.id) || auth0.isAuthenticated;
  const authMethod = resolveAuthMethod({
    hasLegacyUser: Boolean(idToken?.user.id),
    isAuth0Authenticated: auth0.isAuthenticated,
  });

  const logout = useCallback(async () => {
    await performSharedLogout({
      remergeApi,
      isAuth0Authenticated: auth0.isAuthenticated,
      auth0Logout: auth0.logout,
      loginBaseUrl,
      afterClientCleanup: onUnauthenticated,
    });
  }, [auth0.isAuthenticated, auth0.logout, loginBaseUrl, onUnauthenticated, remergeApi]);

  useEffect(syncAuth0ProvisionState, [auth0.isAuthenticated, user?.id, resetProvisionAttempt, clearProvisionedUser]);
  useEffect(syncSsoAuthType, [idToken?.user.id, auth0.isAuthenticated]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auth0 object is stable, individual properties are tracked
  useEffect(redirectUnauthenticatedUsers, [
    isResolvingAuth,
    user?.id,
    auth0.isAuthenticated,
    auth0.loginWithRedirect,
    onUnauthenticated,
    loginBaseUrl,
  ]);
  useEffect(notifyAuthenticated, [user?.id, authMethod, onAuthenticated, user]);

  return {
    user,
    isResolvingAuth,
    isAuthenticated,
    authMethod,
    logout,
  };

  function syncAuth0ProvisionState() {
    if (user?.id) {
      resetProvisionAttempt?.();
      return;
    }

    if (!auth0.isAuthenticated) {
      clearProvisionedUser?.();
      resetProvisionAttempt?.();
    }
  }

  function syncSsoAuthType() {
    if (idToken?.user.id) {
      setSsoAuthType("legacy");
      return;
    }

    if (auth0.isAuthenticated) {
      setSsoAuthType("auth0");
    }
  }

  function redirectUnauthenticatedUsers() {
    if (isResolvingAuth || user?.id || auth0.isAuthenticated) return;

    const authType = getSsoAuthType();
    if (shouldAttemptLocalAuthBootstrap(authType, hasAttemptedAuth0Probe.current)) {
      hasAttemptedAuth0Probe.current = true;
      auth0.loginWithRedirect({
        appState: buildAuth0AppState(window.location.pathname, window.location.search),
      });
      return;
    }

    removeIdToken();
    onUnauthenticated?.();
    redirectToLogin({ loginBaseUrl });
  }

  function notifyAuthenticated() {
    if (!user?.id || authMethod === "none") return;
    onAuthenticated?.(user, authMethod);
  }
}
